import React from 'react'
import * as d3 from 'd3'
import './RuleGraph.css'

interface GraphData {
  book_name: string
  code_name: string
  input_code: string
  code_regulation_id: string
  acronym: Acronym
  chunked_rules: ChunkedRule[]
  graph_json: Analysis
}
interface ChunkedRule {
  category: string
  rules: Rule[]
}

interface Rule {
  index: number
  rule_name: string
  context: string
  content: string
}

interface Analysis {
  nodes: Element
  relationships: Relationship[]
}

interface GraphNode {}

interface Element {
  [type: string]: ElementInfo
}

interface Acronym {
  [type: string]: string
}

interface Relationship {
  source: string
  target: string
  type?: string
  description?: string
  relationshipType?: string
  value?: number
}

interface ElementInfo {
  rules_list: string[]
  category: string
  attributes: string[]
  properties: string[]
  references: string[]
  protocol?: string
}

interface RuleGraphProps {
  data?: GraphData
}

interface RelationshipColor {
  [type: string]: string
}

interface GroupColor {
  [type: number]: string
}

interface BuildingElementsGraphProps {
  data: GraphData
}

const BuildingElementsGraph = ({ data }: BuildingElementsGraphProps) => {
  const svgRef = React.useRef(null)
  const [zoomLevel, setZoomLevel] = React.useState(1)
  const [selectedNode, setSelectedNode] = React.useState(null)
  const [highlightedRule, setHighlightedRule] = React.useState<string | null>()

  const [clientWidth, setClientWidth] = React.useState(window.innerWidth * 0.5)
  const [clientHeight, setClientHeight] = React.useState(window.innerHeight)
  // Create persistent refs for simulation and node/link elements
  const simulationRef = React.useRef<any>(null)
  const nodeRef = React.useRef<any>()
  const linkRef = React.useRef<any>()

  // Function to highlight nodes and links based on rule acronym
  const highlightElementsByRule = (ruleAcronym: string) => {
    if (!nodeRef.current || !linkRef.current) return

    if (!ruleAcronym) {
      // Reset all highlights
      resetHighlights()
      return
    }

    // Find elements that have this rule in their rules_list
    const relatedElements: string[] = []
    Object.entries(data.graph_json.nodes).forEach(
      ([elementName, elementInfo]) => {
        if (elementInfo.rules_list.includes(ruleAcronym)) {
          relatedElements.push(elementName)
        }
      },
    )

    if (relatedElements.length === 0) return

    // Highlight related nodes
    if (nodeRef.current)
      nodeRef.current
        .filter(
          (d: any) =>
            relatedElements.includes(d.id) ||
            relatedElements.includes(d.source) ||
            relatedElements.includes(d.target),
        )
        .select('circle')
        .attr('stroke', '#000')
        .attr('strokeWidth', 3)
        .attr('r', (d: any) => {
          // Make highlighted nodes slightly bigger
          if (d.group === 1) return 14 // Elements
          if (d.group === 2) return 12 // Categories
          if (d.group === 3) return 12 // Protocols
          return 10 // Attributes
        })

    // Determine related links
    linkRef.current
      .attr('stroke', (l: any) => {
        const sourceId = typeof l.source === 'object' ? l.source.id : l.source
        const targetId = typeof l.target === 'object' ? l.target.id : l.target
        return relatedElements.includes(sourceId) ||
          relatedElements.includes(targetId)
          ? '#333'
          : '#999'
      })
      .attr('stroke-opacity', (l: any) => {
        const sourceId = typeof l.source === 'object' ? l.source.id : l.source
        const targetId = typeof l.target === 'object' ? l.target.id : l.target
        return relatedElements.includes(sourceId) ||
          relatedElements.includes(targetId)
          ? 0.8
          : 0.2
      })
      .attr('strokeWidth', (l: any) => {
        const sourceId = typeof l.source === 'object' ? l.source.id : l.source
        const targetId = typeof l.target === 'object' ? l.target.id : l.target
        return relatedElements.includes(sourceId) ||
          relatedElements.includes(targetId)
          ? Math.sqrt(l.value) * 1.5
          : Math.sqrt(l.value) * 0.8
      })

    setHighlightedRule(ruleAcronym)
  }

  // Reset all highlights
  const resetHighlights = () => {
    if (!nodeRef.current || !linkRef.current) return

    nodeRef.current
      .select('circle')
      .attr('stroke', '#fff')
      .attr('strokeWidth', 2)
      .attr('r', (d: any) => {
        // Reset to original sizes
        if (d.group === 1) return 12 // Elements
        if (d.group === 2) return 10 // Categories
        if (d.group === 3) return 10 // Protocols
        return 8 // Attributes
      })

    linkRef.current
      .attr('stroke', '#999')
      .attr('stroke-opacity', 0.4)
      .attr('strokeWidth', (d: any) => Math.sqrt(d.value) * 0.8)

    setHighlightedRule(null)
  }

  const relationshipColors: RelationshipColor = {
    Contains: '#4299e1',
    Measures: '#48bb78',
    Requires: '#ed8936',
    References: '#9f7aea',
  }

  // Rule content component with improved hoverable sections
  const RuleContent = () => {
    const [hoveredRule, setHoveredRule] = React.useState<string | null>(null)

    // Find the acronym for a rule name
    const findAcronym = (ruleName: string) => {
      for (const [acronym, name] of Object.entries(data.acronym)) {
        // Normalize both strings for comparison
        const normalizedName = name.toLowerCase().replace(/_/g, '')
        const normalizedRuleName = ruleName.toLowerCase().replace(/_/g, '')
        if (normalizedName === normalizedRuleName) {
          return acronym
        }
      }
      return null
    }

    // Create sections that match the content field format
    const mainSection = (
      <div className='mb-4'>
        {data?.chunked_rules
          ?.find((chunkedRule) => chunkedRule.category === 'main')
          ?.rules.map((rule, index) => {
            return (
              <div>
                <span
                  className={`highlight-container ${hoveredRule === rule.rule_name ? 'highlight-container-active' : ''}`}
                  onMouseEnter={() => {
                    setHoveredRule(rule.rule_name)
                    const acronym = findAcronym(rule.rule_name)
                    acronym && highlightElementsByRule(acronym)
                  }}
                  onMouseLeave={() => {
                    setHoveredRule(null)
                    resetHighlights()
                  }}
                >
                  <span className='relative z-10'>{rule.content}. </span>
                </span>

                {hoveredRule === rule.rule_name && (
                  <div className='mt-2 text-sm text-blue-600 p-1 bg-blue-50 rounded hover-context border-t border-blue-200'>
                    {rule.context}
                  </div>
                )}
              </div>
            )
          })}
      </div>
    )

    const exceptions = data?.chunked_rules?.find(
      (chunkedRule) => chunkedRule.category === 'exception',
    )?.rules
    // Exceptions header
    const exceptionsHeader = (
      <div className='font-bold mt-4 mb-2'>Exceptions:</div>
    )

    // Exceptions list
    const exceptionsList = (
      <div className='pl-6'>
        {/* Map the exceptions dynamically */}
        {data?.chunked_rules
          ?.find((chunkedRule) => chunkedRule.category === 'exception')
          ?.rules.map((rule, index) => {
            return (
              <div key={index} className='mb-3'>
                <div className='flex'>
                  <span className='mr-2'>{index + 1}.</span>
                  <span
                    className={`highlight-container ${hoveredRule === rule.rule_name ? 'highlight-container-active' : ''}`}
                    onMouseEnter={() => {
                      setHoveredRule(rule.rule_name)
                      const acronym = findAcronym(rule.rule_name)
                      acronym && highlightElementsByRule(acronym)
                    }}
                    onMouseLeave={() => {
                      setHoveredRule(null)
                      resetHighlights()
                    }}
                  >
                    <span className='relative z-10'>{rule?.content}.</span>
                  </span>
                </div>

                {/* Show context for hovered exception */}
                {hoveredRule === rule.rule_name && (
                  <div className='mt-2 ml-4 text-sm text-blue-600 p-1 bg-blue-50 rounded hover-context border-t border-blue-200'>
                    {rule.context}
                  </div>
                )}
              </div>
            )
          })}
      </div>
    )

    return (
      <div className='rule-content'>
        {mainSection}
        {exceptions && exceptions?.length > 0 && exceptionsHeader}
        {exceptionsList}
      </div>
    )
  }
  // D3 graph initialization
  React.useEffect(() => {
    if (!svgRef.current) return

    // Clear any existing SVG content
    d3.select(svgRef.current).selectAll('*').remove()

    // Group colors
    const groupColors: GroupColor = {
      1: '#4e79a7', // Elements
      2: '#f28e2c', // Categories
      3: '#e15759', // Protocols
      4: '#76b7b2', // Attributes
    }

    // Using the relationshipColors defined earlier

    // Create nodes and links for the graph
    const nodes: any[] = []
    const links: Relationship[] = []

    // Create nodes for elements
    Object.entries(data.graph_json.nodes).forEach(
      ([elementName, elementInfo]) => {
        nodes.push({
          id: elementName,
          group: 1, // Elements
          label: elementName,
          rules: elementInfo.rules_list,
          description: `Element: ${elementName}`,
        })

        // Add category node if it doesn't exist
        if (!nodes.some((node) => node.id === elementInfo.category)) {
          nodes.push({
            id: elementInfo.category,
            group: 2, // Categories
            label: elementInfo.category,
            description: `Category: ${elementInfo.category.replace('_', ' ')}`,
          })
        }

        // Link element to category
        links.push({
          source: elementName,
          target: elementInfo.category,
          value: 2,
        })

        // Handle protocol if it exists (added this check)
        if (elementInfo.protocol) {
          if (!nodes.some((node) => node.id === elementInfo.protocol)) {
            nodes.push({
              id: elementInfo.protocol,
              group: 3, // Protocols
              label: elementInfo.protocol,
              description: `Protocol: ${elementInfo.protocol}`,
            })
          }

          // Link element to protocol
          links.push({
            source: elementName,
            target: elementInfo.protocol,
            value: 2,
          })
        }

        // Add attribute nodes and create links
        elementInfo.attributes.forEach((attr: string) => {
          if (!nodes.some((node) => node.id === attr)) {
            nodes.push({
              id: attr,
              group: 4, // Attributes
              label: attr,
              description: `Attribute: ${attr
                .replace(/_/g, ' ')
                .toLowerCase()
                .replace(/\b\w/g, (c) => c.toUpperCase())}`,
            })
          }

          // Link element to attribute
          links.push({
            source: elementName,
            target: attr,
            value: 1,
          })
        })
      },
    )

    // Add relationship links from data
    data.graph_json.relationships.forEach((rel) => {
      // Check if relationship nodes exist
      if (
        nodes.some((n) => n.id === rel.source) &&
        nodes.some((n) => n.id === rel.target)
      ) {
        links.push({
          source: rel.source,
          target: rel.target,
          value: 2, // Keep consistent thickness
          type: rel.type,
          description: rel.description,
          relationshipType: rel.type, // Store explicitly for easier reference
        })
      }
    })

    // Set up SVG dimensions
    const width = 800
    const height = 600

    // Create SVG element
    const svg = d3
      .select(svgRef.current)
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', [0, 0, width, height])
      .style('background', '#f8f9fa')

    // Create group for all elements
    const g = svg.append('g')

    // Function to update label visibility based on zoom level
    const updateLabelsVisibility = (zoomLevel: any) => {
      // Update state for UI display
      setZoomLevel(zoomLevel)

      // Use d3 selection to update all text elements
      svg.selectAll('text').attr('opacity', zoomLevel > 0.65 ? 0.8 : 0)
    }

    // Track connected nodes for highlighting
    const getConnectedNodes = (nodeId: any) => {
      const connected = new Set()
      links.forEach((link) => {
        const sourceId = link.source
        const targetId = link.target

        if (sourceId === nodeId) {
          connected.add(targetId)
        } else if (targetId === nodeId) {
          connected.add(sourceId)
        }
      })
      return connected
    }

    // Add zoom functionality
    const zoomBehavior = d3
      .zoom()
      .extent([
        [0, 0],
        [width, height],
      ])
      .scaleExtent([0.1, 8])
      .on('zoom', (event: any) => {
        // Apply transform to the group
        g.attr('transform', event.transform)

        // Update labels visibility based on zoom level
        updateLabelsVisibility(event.transform.k)
      })

    // Attach zoom behavior to the SVG
    ////svg.call(zoomBehavior);

    // Add double-click behavior to reset zoom
    svg.on('dblclick.zoom', () => {
      // svg.transition()
      //     .duration(750)
      //     .call(zoomBehavior.transform, d3.zoomIdentity);
    })

    // Draw links with colors based on relationship type
    const link = g
      .append('g')
      .selectAll('line')
      .data(links)
      .join('line')
      .attr('stroke', (d: any) => {
        // Safely access the relationship type
        if (!d || typeof d !== 'object') return '#999'
        return relationshipColors[d.relationshipType] || '#999'
      }) // Color by relationship type
      .attr('stroke-opacity', 0.7)
      .attr('strokeWidth', (d: any) => Math.sqrt(d.value) * 1.5) // Make edges thicker for better visibility // Make slightly thicker for visibility

    // Store link selection in ref for external access
    linkRef.current = link

    // Define drag behavior
    function dragstarted(event: any, d: any) {
      if (!event.active) simulationRef?.current?.alphaTarget(0.3).restart()
      d.fx = d.x
      d.fy = d.y
    }

    function dragged(event: any, d: any) {
      d.fx = event.x
      d.fy = event.y
    }

    function dragended(event: any, d: any) {
      if (!event.active) simulationRef.current.alphaTarget(0)
      // Keep the node where it was dropped (better UX)
      d.fx = event.x
      d.fy = event.y
    }

    // Create node group
    const node = g
      .append('g')
      .selectAll('.node')
      .data(nodes)
      .join('g')
      .attr('class', 'node')
      // .call(d3.drag()
      //     .on("start", dragstarted)
      //     .on("drag", dragged)
      //     .on("end", dragended))
      .on('mouseover', function (event: any, d: any) {
        if (event.clientX + 200 > clientWidth)
          setClientWidth(event.clientX + 350)
        else setClientWidth(window.innerWidth * 0.5)
        // Get all connected nodes
        const connectedNodes = getConnectedNodes(d.id)

        // Highlight current node
        d3.select(this)
          .select('circle')
          .attr('stroke', '#000')
          .attr('strokeWidth', 3)

        // Highlight connected nodes
        node
          .filter((n: any) => connectedNodes.has(n.id))
          .select('circle')
          .attr('stroke', '#555')
          .attr('strokeWidth', 2.5)

        // Highlight related links
        link
          .attr('stroke', (l: Relationship) => {
            const sourceId = l.source
            const targetId = l.target
            return sourceId === d.id || targetId === d.id ? '#333' : '#999'
          })
          .attr('stroke-opacity', (l: Relationship) => {
            const sourceId = l.source
            const targetId = l.target
            return sourceId === d.id || targetId === d.id ? 0.8 : 0.2
          })
          .attr('strokeWidth', (l: Relationship) => {
            const sourceId = l.source
            const targetId = l.target
            return sourceId === d.id || targetId === d.id
              ? Math.sqrt(l.value || 0) * 1.5
              : Math.sqrt(l.value || 0) * 0.8
          })

        // Update selected node for info panel
        setSelectedNode(d)

        event.stopPropagation()
      })
      .on('mouseout', function () {
        // If a rule is currently highlighted, don't reset
        if (highlightedRule) return

        // Reset all nodes and links
        node.select('circle').attr('stroke', '#fff').attr('strokeWidth', 2)
        link
          .attr('stroke', (d: any) => {
            if (!d || !d.relationshipType) return '#999'
            return relationshipColors[d.relationshipType] || '#999'
          })
          .attr('stroke-opacity', 0.7)
          .attr('strokeWidth', (d: any) => Math.sqrt(d.value) * 1.5) // Consistent with initial values

        setSelectedNode(null)
      })

    // Store node selection in ref for external access
    nodeRef.current = node

    // Add circles to nodes with larger radii for better visibility
    node
      .append('circle')
      .attr('r', (d: any) => {
        // Different sizes based on node type for better visual hierarchy
        if (d.group === 1) return 12 // Elements slightly larger
        if (d.group === 2) return 10 // Categories
        if (d.group === 3) return 10 // Protocols
        return 8 // Attributes smaller
      })
      .attr('fill', (d: any) => groupColors[d.group])
      .attr('stroke', '#fff')
      .attr('strokeWidth', 2)
      .attr('opacity', 0.9) // Slightly transparent for visual aesthetic

    // Add text labels to nodes (initially hidden, shown on zoom)
    node
      .append('text')
      .attr('dx', 12)
      .attr('dy', '.35em')
      .attr('font-size', '8px')
      .attr('pointer-events', 'none')
      .attr('opacity', 0) // Initially hidden
      .text((d: any) => {
        // Convert to lowercase and capitalize first letter of each word
        const text = d.label.replace(/_/g, ' ')
        return text.toLowerCase().replace(/\b\w/g, (c: any) => c.toUpperCase())
      })

    // Create a force simulation with improved dynamics
    const simulation = d3
      .forceSimulation(nodes)
      .force(
        'link',
        d3
          .forceLink(links)
          .id((d: any) => d.id)
          .distance(120),
      )
      .force('charge', d3.forceManyBody().strength(-300))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('x', d3.forceX(width / 2).strength(0.07))
      .force('y', d3.forceY(height / 2).strength(0.07))
      .force('collision', d3.forceCollide().radius(20).strength(0.7))

    // Store simulation in ref for access from drag function
    simulationRef.current = simulation

    // Set cooling parameters to stabilize the simulation faster
    simulation.alphaDecay(0.028) // Default is 0.0228
    simulation.velocityDecay(0.4) // Default is 0.4, increase to slow down movement

    // Update positions on each tick
    simulation.on('tick', () => {
      link
        .attr('x1', (d: any) => d.source.x)
        .attr('y1', (d: any) => d.source.y)
        .attr('x2', (d: any) => d.target.x)
        .attr('y2', (d: any) => d.target.y)

      node.attr('transform', (d: any) => `translate(${d.x},${d.y})`)
    })

    // Set initial label visibility
    updateLabelsVisibility(1)

    // Cleanup function
    return () => {
      if (simulationRef.current) {
        simulationRef.current.stop()
      }
    }
  }, [])

  const getRelationshipTypeName = (type: string) => {
    return type || 'Unknown'
  }
  // Info panel component
  const InfoPanel = ({ node }: any) => {
    if (!node) return null

    // Get a descriptive title based on node type
    const getNodeTypeTitle = () => {
      switch (node.group) {
        case 1:
          return 'Element'
        case 2:
          return 'Category'
        case 3:
          return 'Protocol'
        case 4:
          return 'Attribute'
        default:
          return 'Node'
      }
    }

    let info
    if (node.group === 1) {
      // Element node details
      const element = data?.graph_json?.nodes?.[node.id]
      if (element) {
        // List related elements: other elements in the same category
        const relatedElements = Object.entries(data.graph_json.nodes)
          .filter(
            ([key, info]) =>
              key !== node.id && info.category === element.category,
          )
          .map(([key]) => key)

        // Compute rule info for the element's rules
        const ruleInfo = element.rules_list.map((acronym: string) => {
          const fullRuleName = data.acronym[acronym]
          const matchingRule = data.chunked_rules
            ?.flatMap((chunkedRule) => chunkedRule.rules)
            .find(
              (rule) =>
                fullRuleName &&
                fullRuleName.toLowerCase() === rule.rule_name.toLowerCase(),
            )
          const context = matchingRule ? matchingRule.context : ''
          return { acronym, context }
        })

        info = (
          <>
            <h3 className='font-bold mb-2'>{node.label.replace(/_/g, ' ')}</h3>
            <div className='text-xs text-gray-500 mb-2'>
              {getNodeTypeTitle()}
            </div>
            <div className='mb-2'>
              <span className='font-semibold'>Category:</span>{' '}
              {element.category.replace(/_/g, ' ')}
            </div>
            {element.protocol && (
              <div className='mb-2'>
                <span className='font-semibold'>Protocol:</span>{' '}
                {element.protocol.replace(/_/g, ' ')}
              </div>
            )}
            {element.attributes && element.attributes.length > 0 && (
              <div className='mb-2'>
                <span className='font-semibold'>Attributes:</span>{' '}
                {element.attributes
                  .map((attr: string) => attr.replace(/_/g, ' '))
                  .join(', ')}
              </div>
            )}
            <div className='mb-2'>
              <span className='font-semibold'>Associated Rules:</span>
              <ul className='list-disc pl-5 mt-1'>
                {ruleInfo.map(({ acronym, context }) => (
                  <li key={acronym}>{context || acronym}</li>
                ))}
              </ul>
            </div>
            <div className='mb-2'>
              <span className='font-semibold'>Related Elements:</span>
              <ul className='list-disc pl-5 mt-1'>
                {relatedElements.length > 0 ? (
                  relatedElements.map((elem, index) => (
                    <li key={index}>
                      {elem.replace(/_/g, ' ')}
                      {data.graph_json.relationships
                        .filter(
                          (rel) =>
                            (rel.source === node.id && rel.target === elem) ||
                            (rel.source === elem && rel.target === node.id),
                        )
                        .map((rel, idx) => (
                          <span
                            key={idx}
                            className='text-xs ml-2'
                            style={{
                              color: relationshipColors[rel.type!] || '#666',
                            }}
                          >
                            ({getRelationshipTypeName(rel.type!)})
                          </span>
                        ))}
                    </li>
                  ))
                ) : (
                  <li>None</li>
                )}
              </ul>
            </div>
          </>
        )
      } else {
        info = (
          <>
            <h3 className='font-bold mb-2'>{node.label.replace(/_/g, ' ')}</h3>
            <div className='text-xs text-gray-500 mb-2'>
              {getNodeTypeTitle()}
            </div>
          </>
        )
      }
    } else {
      // For non-element nodes (Category, Protocol, Attribute)
      let relatedElements: string[] = []
      if (node.group === 2) {
        // Category: list elements in this category
        relatedElements = Object.entries(data.graph_json.nodes)
          .filter(([_, elementInfo]) => elementInfo.category === node.id)
          .map(([elementName]) => elementName)
      } else if (node.group === 3) {
        // Protocol: list elements using this protocol
        relatedElements = Object.entries(data.graph_json.nodes)
          .filter(([_, elementInfo]) => elementInfo.protocol === node.id)
          .map(([elementName]) => elementName)
      } else if (node.group === 4) {
        // Attribute: list elements having this attribute
        relatedElements = Object.entries(data.graph_json.nodes)
          .filter(([_, elementInfo]) =>
            elementInfo.attributes.includes(node.id),
          )
          .map(([elementName]) => elementName)
      }

      info = (
        <>
          <h3 className='font-bold mb-2'>{node.label.replace(/_/g, ' ')}</h3>
          <div className='text-xs text-gray-500 mb-2'>{getNodeTypeTitle()}</div>
          <div className='mb-2'>
            <span className='font-semibold'>Related Elements:</span>
            <ul className='list-disc pl-5 mt-1'>
              {relatedElements.length > 0 ? (
                relatedElements.map((elem, index) => (
                  <li key={index}>{elem.replace(/_/g, ' ')}</li>
                ))
              ) : (
                <li>None</li>
              )}
            </ul>
          </div>
        </>
      )
    }

    // Return the info content directly without additional styling since it's in a modal now
    return <div>{info}</div>
  }

  return (
    <div className='flex flex-col h-screen'>
      {/* Main graph taking up most of the screen */}
      <div className='flex-1 bg-white shadow-md relative'>
        <div style={{ width: '70%' }}>
          <svg ref={svgRef} className='h-full'></svg>
        </div>
        {/* Enhanced Node Type Info Panel */}
        <div className='absolute bottom-2 left-2 text-xs'>
          <div className='bg-white/90 p-3 rounded-lg shadow-md border border-gray-200'>
            <div className='text-center mb-3 font-bold text-sm pb-2 border-b border-gray-200'>
              Graph Legend
            </div>
            <h3 className='font-semibold text-sm mb-2 text-gray-700'>
              Node Types
            </h3>
            <div className='grid grid-cols-1 gap-2'>
              <div className='flex items-center p-1 hover:bg-blue-50 rounded transition-colors'>
                <div className='w-3 h-3 bg-blue-500 rounded-full mr-2 shadow-inner'></div>
                <div>
                  <span className='text-gray-800 font-medium'>Elements</span>
                  <p className='text-xs text-gray-500'>
                    Building code components
                  </p>
                </div>
              </div>
              <div className='flex items-center p-1 hover:bg-orange-50 rounded transition-colors'>
                <div className='w-3 h-3 bg-orange-500 rounded-full mr-2 shadow-inner'></div>
                <div>
                  <span className='text-gray-800 font-medium'>Categories</span>
                  <p className='text-xs text-gray-500'>
                    Element classifications
                  </p>
                </div>
              </div>
              <div className='flex items-center p-1 hover:bg-red-50 rounded transition-colors'>
                <div className='w-3 h-3 bg-red-500 rounded-full mr-2 shadow-inner'></div>
                <div>
                  <span className='text-gray-800 font-medium'>Protocols</span>
                  <p className='text-xs text-gray-500'>
                    Enforcement procedures
                  </p>
                </div>
              </div>
              <div className='flex items-center p-1 hover:bg-teal-50 rounded transition-colors'>
                <div className='w-3 h-3 bg-teal-500 rounded-full mr-2 shadow-inner'></div>
                <div>
                  <span className='text-gray-800 font-medium'>Attributes</span>
                  <p className='text-xs text-gray-500'>Element properties</p>
                </div>
              </div>
            </div>

            <h3 className='font-semibold text-sm mt-4 mb-3 text-gray-700'>
              Edge Types
            </h3>
            <div className='grid grid-cols-1 gap-3'>
              <div className='flex items-center p-2 hover:bg-blue-50 rounded transition-colors'>
                <svg width='30' height='12'>
                  <line
                    x1='0'
                    y1='6'
                    x2='30'
                    y2='6'
                    stroke='#4299e1'
                    strokeWidth='3'
                  />
                </svg>
                <div className='ml-3'>
                  <span
                    className='text-gray-800 font-medium'
                    style={{ color: '#4299e1' }}
                  >
                    Contains
                  </span>
                  <p className='text-xs text-gray-500'>
                    Parent-child relationship
                  </p>
                </div>
              </div>
              <div className='flex items-center p-2 hover:bg-green-50 rounded transition-colors'>
                <svg width='30' height='12'>
                  <line
                    x1='0'
                    y1='6'
                    x2='30'
                    y2='6'
                    stroke='#48bb78'
                    strokeWidth='3'
                  />
                </svg>
                <div className='ml-3'>
                  <span
                    className='text-gray-800 font-medium'
                    style={{ color: '#48bb78' }}
                  >
                    Measures
                  </span>
                  <p className='text-xs text-gray-500'>
                    Quantitative relationship
                  </p>
                </div>
              </div>
              <div className='flex items-center p-2 hover:bg-orange-50 rounded transition-colors'>
                <svg width='30' height='12'>
                  <line
                    x1='0'
                    y1='6'
                    x2='30'
                    y2='6'
                    stroke='#ed8936'
                    strokeWidth='3'
                  />
                </svg>
                <div className='ml-3'>
                  <span
                    className='text-gray-800 font-medium'
                    style={{ color: '#ed8936' }}
                  >
                    Requires
                  </span>
                  <p className='text-xs text-gray-500'>
                    Dependency relationship
                  </p>
                </div>
              </div>
              <div className='flex items-center p-2 hover:bg-purple-50 rounded transition-colors'>
                <svg width='30' height='12'>
                  <line
                    x1='0'
                    y1='6'
                    x2='30'
                    y2='6'
                    stroke='#9f7aea'
                    strokeWidth='3'
                  />
                </svg>
                <div className='ml-3'>
                  <span
                    className='text-gray-800 font-medium'
                    style={{ color: '#9f7aea' }}
                  >
                    References
                  </span>
                  <p className='text-xs text-gray-500'>
                    Informational relationship
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Floating info panel that appears on hover - now directly over the graph */}
        {selectedNode && (
          <div
            className='absolute transform -translate-x-1.6/2 -translate-y-1/2 w-80 z-20'
            style={{
              left: `${clientWidth / 2}px`,
              top: `${clientHeight / 2}px`,
            }}
          >
            <div className='bg-white/95 rounded-lg shadow-xl border border-gray-200 p-5'>
              <button
                className='absolute top-2 right-2 text-gray-500 hover:text-gray-800 close-button'
                onClick={() => setSelectedNode(null)}
              >
                ×
              </button>
              <InfoPanel node={selectedNode} />
            </div>
          </div>
        )}

        {/* Overlay panel for rules on the right side */}
        <div className='absolute top-0 right-0 w-1/3 h-full bg-gray-50/95 shadow-lg overflow-y-auto border-l border-gray-200'>
          <div className='p-6'>
            <div className='font-bold text-xl mb-2 text-blue-800'>
              {data.code_regulation_id}
              {' ' + data.code_name}
            </div>
            <div className='text-sm text-gray-600 mb-4'>{data.book_name}</div>
            <RuleContent />
          </div>
        </div>
      </div>
    </div>
  )
}

export default function RuleGraph({ data }: RuleGraphProps) {
  return (
    <div>
      <BuildingElementsGraph data={data!} />
    </div>
  )
}
